import React, {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import {useDispatch} from "useDispatch";
import {closeModal} from "helpers/actions";
import FeatherIcon from "components/FeatherIcon";

type Ride = {id: string; label: string; credits: number; img: string};
type Person = {type: "adult" | "kid"; counts: Record<string, number>};
type Pack = {credits: number; price: number; rabatt: string; rides: string; family: boolean};

const rides: Ride[] = [
    {id: "kart", label: "Go-Kart", credits: 18, img: "/img/credits/credits-go-karting.svg"},
    {id: "drift", label: "Crazy Kart", credits: 6, img: "/img/credits/credits-drift.svg"},
    {id: "zosh", label: "Super-Drift", credits: 18, img: "/img/credits/credits-zosh.svg"},
];
const packs: Pack[] = [
    {credits: 24, price: 19.2, rabatt: "−20%", rides: "1–5 rides", family: false},
    {credits: 36, price: 25.2, rabatt: "−30%", rides: "2–9 rides", family: false},
    {credits: 64, price: 38.4, rabatt: "−40%", rides: "3–16 rides", family: false},
    {credits: 96, price: 52.8, rabatt: "−45%", rides: "5–24 rides", family: true},
    {credits: 128, price: 67.2, rabatt: "−47.5%", rides: "7–32 rides", family: true},
    {credits: 256, price: 128, rabatt: "−50%", rides: "14–64 rides", family: true},
];
const money = (value: number) => `CHF ${value.toFixed(2)}`;
const person = (type: Person["type"] = "adult"): Person => ({type, counts: {}});

const CreditCalculatorModal = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [mode, setMode] = useState<"single" | "family">("single");
    const [people, setPeople] = useState<Person[]>([person()]);
    const activePacks = useMemo(() => packs.filter(pack => mode === "family" || !pack.family), [mode]);
    const creditsFor = (p: Person) => rides.reduce((sum, ride) => sum + (p.counts[ride.id] || 0) * ride.credits, 0);
    const total = people.reduce((sum, p) => sum + creditsFor(p), 0);
    const recommendation = activePacks.find(pack => pack.credits >= total) || activePacks[activePacks.length - 1];
    const update = (index: number, ride: Ride, amount: number) =>
        setPeople(current =>
            current.map((p, i) =>
                i === index
                    ? {
                          ...p,
                          counts: {...p.counts, [ride.id]: Math.max(0, (p.counts[ride.id] || 0) + amount)},
                      }
                    : p
            )
        );
    const selectMode = (next: "single" | "family") => {
        setMode(next);
        setPeople(next === "family" ? [person(), person("kid")] : [person()]);
    };

    const closeModalHandler = () => {
        dispatch(closeModal());
    };

    return (
        <div className="mx-auto bg-transparent flex flex-col h-full max-h-[100dvh] min-h-0 overflow-hidden w-screen px-3 sm:px-6 max-w-screen-lg justify-center flex-1">
            <div className={"bg-white rounded-lg font-poppins flex flex-col h-full max-h-full min-h-0 overflow-hidden"}>
                <div className={"bg-arbo-red relative flex flex-row"}>
                    <div
                        className={
                            "w-full text-white text-center flex items-center pl-2 sm:pl-0 justify-start sm:justify-center font-bold text-base sm:text-xl md:text-3xl uppercase h-20 shrink-0"
                        }
                    >
                        {t("How many credits do I need?")}
                    </div>
                    <button
                        className={clsx("print:hidden p-6 bg-red-500 text-white absolute right-0 top-0 z-[100] h-18 rounded-tr-lg")}
                        onClick={closeModalHandler}
                    >
                        <FeatherIcon className={"size-8 flex-none text-white hover:opacity-70"} name={"x"} />
                    </button>
                </div>

                <div className="p-[20px_14px] sm:p-7 sm:px-6 min-h-0 flex-1 overflow-y-auto scrollbar flex flex-col">
                    <div className="grid grid-cols-1 gap-[14px] sm:grid-cols-3">
                        {rides.map(ride => (
                            <div key={ride.id} className="flex items-center gap-[14px] rounded-[14px] border border-[#E4E4E8] p-3 pr-[18px]">
                                <img alt={t(ride.label)} className="h-16 w-16 flex-none rounded-xl object-cover" src={ride.img} />
                                <div>
                                    <div className="text-base font-bold">{t(ride.label)}</div>
                                    <div className="text-[13px] text-arbo-gray">
                                        {t("1 ride")} —{" "}
                                        <b className="text-arbo-red">
                                            {ride.credits} {t("Credits")}
                                        </b>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-6 sm:mt-[34px]">
                        <div className="grid grid-cols-2 gap-2.5 sm:flex">
                            {(["single", "family"] as const).map(item => (
                                <button
                                    key={item}
                                    aria-pressed={mode === item}
                                    className={`rounded-[10px] border px-4 py-3 text-base font-semibold shadow-[0_3px_10px_rgba(0,0,0,.08)] sm:px-6 ${
                                        mode === item ? "border-arbo-red bg-arbo-red text-white" : "border-[#E4E4E8] bg-white"
                                    }`}
                                    type="button"
                                    onClick={() => selectMode(item)}
                                >
                                    {t(item === "single" ? "Individual" : "Family")}
                                </button>
                            ))}
                        </div>
                        <p className="mt-2.5 text-[13.5px] leading-[1.5] text-[#38383F]">
                            {t(mode === "family" ? "credit_calculator.family_description" : "credit_calculator.single_description")}
                        </p>
                    </div>
                    <div className="mt-[14px] rounded-2xl bg-[#F4F4F6] p-4 sm:p-[22px_26px]">
                        {mode === "single" && <div className="mb-2.5 text-lg font-bold">{t("What would you like to ride?")}</div>}
                        {mode === "family" && (
                            <div className="mb-2 hidden grid-cols-[minmax(80px,1fr)_minmax(115px,1fr)_minmax(115px,1fr)_minmax(115px,1fr)_60px] gap-3 px-4 text-[11.5px] font-semibold uppercase tracking-[.04em] text-[#70707A] sm:grid lg:grid-cols-[minmax(150px,1fr)_minmax(150px,1.2fr)_minmax(150px,1.2fr)_minmax(150px,1.2fr)_150px]">
                                <span>{t("Person")}</span>
                                {rides.map(ride => (
                                    <span key={ride.id} className="text-center">
                                        {t(ride.label)} · {ride.credits}
                                    </span>
                                ))}
                                <span className="text-right">{t("Credits")}</span>
                            </div>
                        )}
                        {mode === "single" &&
                            people.map((p, pi) => (
                                <div key={pi} className="mb-2 rounded-[10px] bg-white p-2 xs:p-3.5 sm:p-[9px_16px]">
                                    {rides.map(ride => {
                                        const value = p.counts[ride.id] || 0;
                                        return (
                                            <div key={ride.id} className="flex items-center justify-between gap-2 xs:gap-3 py-1">
                                                <span
                                                    className={`text-black flex justify-between w-full ${
                                                        mode === "single" ? "text-sm font-semibold" : "text-sm"
                                                    }`}
                                                >
                                                    {t(ride.label)}
                                                    {mode === "single" && (
                                                        <small className="ml-2 text-[13px] font-normal text-arbo-gray">
                                                            {ride.credits} {t("Credits")}
                                                        </small>
                                                    )}
                                                </span>
                                                <span className="flex items-center gap-2">
                                                    <button
                                                        className={
                                                            "h-[34px] w-[34px] rounded-[9px] border border-[#E4E4E8] bg-white text-base font-bold disabled:opacity-30"
                                                        }
                                                        disabled={!value}
                                                        type="button"
                                                        onClick={() => update(pi, ride, -1)}
                                                    >
                                                        <span>−</span>
                                                    </button>
                                                    <span
                                                        className={`min-w-[20px] xs:min-w-[30px] text-center text-sm font-bold ${
                                                            !value ? "text-arbo-gray" : ""
                                                        }`}
                                                    >
                                                        {value}
                                                    </span>
                                                    <button
                                                        className="h-[34px] w-[34px] rounded-[9px] border border-[#E4E4E8] bg-white text-base font-bold"
                                                        type="button"
                                                        onClick={() => update(pi, ride, 1)}
                                                    >
                                                        <span>+</span>
                                                    </button>
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        {mode === "family" &&
                            people.map((p, pi) => (
                                <div
                                    key={pi}
                                    className="mb-2 grid grid-cols-1 gap-2 rounded-[10px] bg-white p-3.5 sm:grid-cols-[minmax(80px,1fr)_minmax(115px,1fr)_minmax(115px,1fr)_minmax(115px,1fr)_60px] sm:items-center sm:gap-3 sm:px-4 sm:py-[9px] lg:grid-cols-[minmax(150px,1fr)_minmax(150px,1.2fr)_minmax(150px,1.2fr)_minmax(150px,1.2fr)_150px]"
                                >
                                    <div className="flex items-center justify-start text-black text-sm font-semibold sm:justify-between sm:block">
                                        {t(p.type === "adult" ? "Adult" : "Child")}
                                        {people.filter(x => x.type === p.type).length > 1 && (
                                            <button
                                                className="ml-2 border-0 bg-transparent text-[#B5B5BC]"
                                                type="button"
                                                onClick={() => setPeople(current => current.filter((_, i) => i !== pi))}
                                            >
                                                ✕
                                            </button>
                                        )}
                                        <span className="float-right ml-auto sm:ml-0 sm:hidden">
                                            {creditsFor(p)} {t("Credits")}
                                        </span>
                                    </div>
                                    {rides.map(ride => {
                                        const value = p.counts[ride.id] || 0;
                                        return (
                                            <div key={ride.id} className="flex items-center justify-between sm:justify-center">
                                                <span className="text-sm text-[#38383F] sm:hidden">{t(ride.label)}</span>
                                                <span className="flex items-center gap-2">
                                                    <button
                                                        className="h-[34px] w-[34px] rounded-[9px] border border-[#E4E4E8] bg-white text-base font-bold disabled:opacity-30"
                                                        disabled={!value}
                                                        type="button"
                                                        onClick={() => update(pi, ride, -1)}
                                                    >
                                                        −
                                                    </button>
                                                    <span
                                                        className={`min-w-[20px] xs:min-w-[30px] text-center text-sm font-bold ${
                                                            !value ? "text-arbo-gray" : ""
                                                        }`}
                                                    >
                                                        {value}
                                                    </span>
                                                    <button
                                                        className="h-[34px] w-[34px] rounded-[9px] border border-[#E4E4E8] bg-white text-base font-bold"
                                                        type="button"
                                                        onClick={() => update(pi, ride, 1)}
                                                    >
                                                        +
                                                    </button>
                                                </span>
                                            </div>
                                        );
                                    })}
                                    <div className="hidden text-right text-sm font-bold sm:block">{creditsFor(p)}</div>
                                </div>
                            ))}
                        {mode === "family" && (
                            <div className="mb-2 flex justify-center gap-6 rounded-[10px] border border-dashed border-[#E4E4E8] bg-white p-2.5 text-[13.5px] font-semibold">
                                <button
                                    className="border-0 bg-transparent"
                                    type="button"
                                    onClick={() => people.length < 10 && setPeople([...people, person("adult")])}
                                >
                                    + {t("Adult")}
                                </button>
                                <button
                                    className="border-0 bg-transparent"
                                    type="button"
                                    onClick={() => people.length < 10 && setPeople([...people, person("kid")])}
                                >
                                    + {t("Child")}
                                </button>
                            </div>
                        )}
                        <div className="flex flex-wrap items-baseline justify-between gap-1.5 pt-2">
                            <span>
                                {t("Required:")}{" "}
                                <b className="text-[21px]">
                                    {total} {t("Credits")}
                                </b>
                            </span>
                            {total ? (
                                <span className="text-[15px] font-bold text-arbo-red">
                                    → {recommendation.credits} {t("Credits")} · {money(recommendation.price)}
                                    {recommendation.credits > total ? ` · ${recommendation.credits - total} ${t("remaining")}` : ""}
                                </span>
                            ) : (
                                <span className="text-[13.5px] text-arbo-gray">{t("Choose above.")}</span>
                            )}
                        </div>
                        {total > recommendation.credits && (
                            <p className="mt-2 text-[13px] text-[#38383F]">{t("You need more than the largest pack — you can top up directly in the park.")}</p>
                        )}
                    </div>
                    <div className="mt-[26px] sm:mt-9">
                        <h4 className="mb-2.5 text-center text-xl font-bold">{t("Credit Packs")}</h4>
                        {activePacks.map(pack => (
                            <div
                                key={pack.credits}
                                className={`mb-2 grid grid-cols-2 items-center gap-x-2 gap-y-[3px] rounded-[10px] border p-[11px_16px] text-[13.5px] sm:grid-cols-[minmax(0,1.3fr)_minmax(0,.8fr)_minmax(0,.7fr)_minmax(0,1fr)_minmax(0,1.25fr)] lg:grid-cols-[110px_100px_70px_115px_130px_minmax(150px,1fr)] ${
                                    pack.credits === recommendation.credits && total ? "border-arbo-red bg-[#FEF4F4]" : "border-[#E4E4E8]"
                                }`}
                            >
                                <b>
                                    {pack.credits} {t("Credits")}
                                </b>
                                <b className="text-right font-semibold sm:text-left">{money(pack.price)}</b>
                                <span className="font-semibold text-green-600">{pack.rabatt}</span>
                                <span className="hidden text-[#70707A] lg:block">
                                    {(pack.price / pack.credits).toFixed(2)} {t("/ Credit")}
                                </span>
                                <span className="text-right text-[#38383F] sm:text-left">{t(pack.rides)}</span>
                                <div className="justify-self-end hidden sm:block">
                                    <span
                                        className={clsx(
                                            "hidden whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-bold sm:inline-flex",
                                            pack.family ? "bg-green-50 text-green-600" : "bg-[#FFF1CC] text-[#8C6605]"
                                        )}
                                    >
                                        {t(pack.family ? "Family & Happy Kids" : "All tickets")}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="text-[12.5px] leading-[1.5] text-arbo-gray">
                        {t(
                            mode === "family"
                                ? "The large shareable packs are exclusive to Family Ticket, Happy Kids and Happy Kids plus — they go into the shared family balance."
                                : "Larger shareable packs are available in Family mode — with Family Ticket, Happy Kids and Happy Kids plus."
                        )}
                    </p>
                    <div className="mt-7 grid grid-cols-1 gap-[14px] lg:mt-[38px] lg:grid-cols-3">
                        {[
                            [
                                "Decide in the park",
                                "When buying, you only choose the quantity. Decide which rides to take on site — spontaneously, at the attraction.",
                            ],
                            ["Credits never expire", "Remaining credits stay in your account and wait for your next visit. You cannot buy too many."],
                            [
                                "Sharing? Families & birthday parties",
                                "With Family Ticket, Happy Kids and Happy Kids plus, the balance is shared — optionally with credit limits per person. With all other tickets, credits are personal.",
                            ],
                        ].map(([title, text], i) => (
                            <div key={title} className={`rounded-xl p-[18px] ${i === 2 ? "border-[1.5px] border-arbo-red" : "bg-[#F4F4F6]"}`}>
                                <h5 className={`mb-1.5 text-[14.5px] font-bold ${i === 2 ? "text-arbo-red" : ""}`}>{t(title)}</h5>
                                <p className="text-[12.5px] leading-[1.5] text-[#38383F]">{t(text)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
export default CreditCalculatorModal;
